// OneDrive:CoverageThreshold(80)

import Promise from '@ms/odsp-utilities/lib/async/Promise';
import {
  IPeoplePickerProvider,
  IPeoplePickerProviderParams
} from '../IPeoplePickerProvider';
import { IPeoplePickerCapabilities } from '../IPeoplePickerCapabilities';
import { IPeoplePickerQueryParams } from '../IPeoplePickerQueryParams';
import { IPeoplePickerDataSource } from '../../../dataSources/peoplePicker/IPeoplePickerDataSource';
import { PeoplePickerDataSource } from '../../../dataSources/peoplePicker/spo/PeoplePickerDataSource';
import { IPeoplePickerProviderResults } from '../IPeoplePickerProviderResults';
import { IPerson, IPersonProfile } from '../../../dataSources/peoplePicker/IPerson';
import { IDisposable } from '@ms/odsp-utilities/lib/interfaces/IDisposable';
import { PeopleStore } from '../../../models/store/PeopleStore';
import { IPickerEntityInformation } from '../../../dataSources/peoplePicker/IPickerEntityInformation';

const UNVALIDATED_EMAIL_ADDRESS = 'UNVALIDATED_EMAIL_ADDRESS'; // Person PrincipalType value to ignore when adding to cache.

export class PeoplePickerProvider implements IPeoplePickerProvider {
  /* tslint:disable */
  public readonly MaxCacheSuggestions: number;
  /* tslint:enable */
  private readonly _dataSource: IPeoplePickerDataSource;
  private _ppCapabilities: IPeoplePickerCapabilities;
  private readonly _mruCache: PeopleStore;

  constructor(params: IPeoplePickerProviderParams) {
    this._dataSource = params.peoplePickerDataSource ? params.peoplePickerDataSource :
      new PeoplePickerDataSource({
        imageAbsoluteWebUrl: params.imageAbsoluteWebUrl
      },
        {
          pageContext: params.pageContext,
          apiUrlHelper: params.apiUrlHelper
        });

    this._ppCapabilities = this._dataSource.getCapabilities();

    if (this._ppCapabilities.supportsMruCaching) {
      this._mruCache = new PeopleStore();
      this.MaxCacheSuggestions = 5;
    } else {
      this.MaxCacheSuggestions = 0;
    }
  }

  public search(query: string, context: IPeoplePickerQueryParams): IPeoplePickerProviderResults {
    let searchPromise: Promise<void>;

    const results: IPeoplePickerProviderResults = {
      cached: null,
      promise: null
    };

    let cacheResults: IPerson[];

    // Send cached results to caller immediately without waiting for server results
    // Don't use cache if limiting results to members of a particular SP Group
    // (context.groupID of zero indicates there are no group membership restrictions)
    if (this._mruCache && !context.groupID) {
      cacheResults = this.searchMruCache(query, context && context.filterExternalUsers);  // get the items from the cache for the current query
      if (cacheResults.length > 0) {
        results.cached = cacheResults;
      }
    }

    results.promise = new Promise((complete: (data: IPerson[]) => void, error: (data: any) => void) => {
      // Call the server to search for people that match the query
      searchPromise = this._dataSource.search(query, context).then((data: Array<IPerson>) => {
        if (!data) {
          return;
        }

        if (cacheResults) {
          // Omit entries that are already in the local cache.
          if (cacheResults.length > 0) {
            cacheResults = this._removeStaleCacheResults(cacheResults, data);
          }
          const cachedIds = cacheResults.map((cacheResult: IPerson) => cacheResult.userId);
          complete(data.filter((person: IPerson) => cachedIds.indexOf(person.userId) < 0));
        } else {
          complete(data);
        }
      }, /* onError */(data: any): void => {
        // pass through error to parent promise
        error(data);
      });
    }, /* onCancel */(): void => {
      if (searchPromise) {
        searchPromise.cancel();
      }
    });

    return results;
  }

  public resolve(query: string, context: IPeoplePickerQueryParams): Promise<IPerson> {
    return this._dataSource.resolve(query, context).then((data: IPerson) => {
      if (this._mruCache && data.isResolved) {
        this.addToMruCache(data);
      }

      return data;
    });

    // TODO: Only add item to the cache for certain query parameter values:
    //  return (this.UseLocalSuggestionCache) &&
    // (this.UrlZone == null) &&
    // (this.SharePointGroupID <= 0) &&
    // (this.WebApplicationID == '{00000000-0000-0000-0000-000000000000}') &&
    // (this.EnabledClaimProviders == '' || this.EnabledClaimProviders == null) &&
    // (this.PrincipalAccountTypeEnum % 2 == 1 /*SP.Utilities.PrincipalType.user*/) &&
    // (this.ResolvePrincipalSource == 15 /*SP.Utilities.PrincipalSource.all*/);
  }

  public getPersonaDetails(accountID: string): Promise<IPersonProfile> {
    return this._dataSource.getPersonaDetails(accountID).then((data: IPersonProfile) => {
      return data;
    });
  }

  public subscribePresence(accountID: string, fnCallback?: () => void): IDisposable {
    return this._dataSource.subscribePresence(accountID, fnCallback);
  }

  public addToMruCache(item: IPerson): void {
    const mruCache = this._mruCache;
    if (mruCache && item.rawPersonData && item.rawPersonData['EntityData'] && item.rawPersonData['EntityData']['PrincipalType'] !== UNVALIDATED_EMAIL_ADDRESS) {
      mruCache.setItem(item);
    }
  }

  public getPickerEntityInformation(person: IPerson): Promise<IPickerEntityInformation> {
    return this._dataSource.getPickerEntityInformation(person);
  }

  public sharePointSearch(query: string, context: IPeoplePickerQueryParams): Promise<IPerson[]> {
    return this._dataSource.sharePointSearch(query, context).then((data: IPerson[]) => {
      return data;
    });
  }

  public canUseGraph(context: IPeoplePickerQueryParams): boolean {
    return this._dataSource.canUseGraph(context);
  }

  public canUseSubstrate(context: IPeoplePickerQueryParams): boolean {
    return this._dataSource.canUseSubstrate(context);
  }

  private searchMruCache(query: string, filterExternalUsers?: boolean): Array<IPerson> {
    let data: Array<IPerson> = [];

    const mruCache = this._mruCache;
    if (mruCache) {
      data = mruCache.getItems(query, filterExternalUsers);
      // Only return up to the maximum number of suggestions requested
      const maxCacheSuggestions = this.MaxCacheSuggestions;
      if (data.length > maxCacheSuggestions) {
        data = data.splice(0, maxCacheSuggestions);
      }
    }

    return data;
  }

  /**
* Removes any stale results from the people picker cache so
* we don't ignore search results that are fresher than the
* corresponding cache entry.
*
* @param cacheResults The results that are stored in the localStorage cache.
* @param searchResults Fresh search results.
*/
  private _removeStaleCacheResults(cacheResults: IPerson[], searchResults: IPerson[]): IPerson[] {
    const cachedIds = cacheResults.map((cacheResult: IPerson) => cacheResult.userId);
    const freshCacheResults: IPerson[] = [];

    searchResults.forEach((searchResult: IPerson) => {
      const cacheResultId = cachedIds.indexOf(searchResult.userId);

      // If the ID exists in the cache, check that it's fresh.
      if (cacheResultId > -1) {
        const cacheResult = cacheResults[cacheResultId];

        // If the cache entry is fresh, add it to the array of fresh cache entries.
        if (PeopleStore.isCacheEntryFresh(cacheResult, searchResult)) {
          freshCacheResults.push(cacheResult);
        }
      }
    });

    return freshCacheResults;
  }
}